import type { AppRuntimeConfig } from "./auth-core.types";

let embeddedRuntimeConfigOverrides: Partial<AppRuntimeConfig> = {};

declare global {
  interface Window {
    APP_CONFIG?: AppRuntimeConfig;
  }
}

/** Stable identity for "no `window.APP_CONFIG`" so the parse cache can key on it. */
const EMPTY_BASE: AppRuntimeConfig = {};

let cache: {
  base: AppRuntimeConfig;
  overrides: Partial<AppRuntimeConfig>;
  value: AppRuntimeConfig;
} | null = null;

function mergeRuntimeConfig(
  baseConfig: AppRuntimeConfig,
  overrideConfig: Partial<AppRuntimeConfig>
): AppRuntimeConfig {
  return {
    ...baseConfig,
    ...overrideConfig,
    auth0: {
      ...baseConfig.auth0,
      ...overrideConfig.auth0,
    },
    requireClaim: {
      ...baseConfig.requireClaim,
      ...overrideConfig.requireClaim,
    },
  };
}

/**
 * The merged, effective runtime config (`window.APP_CONFIG` + embedded-host
 * overrides). Parsed once and reused until either input's *reference* changes
 * — `stubAppConfig()` in tests assigns a fresh object, so it invalidates
 * naturally; `setEmbeddedRuntimeConfigOverrides()` does too.
 */
export function getRuntimeConfig(): AppRuntimeConfig {
  const base = window.APP_CONFIG ?? EMPTY_BASE;
  if (cache && cache.base === base && cache.overrides === embeddedRuntimeConfigOverrides) {
    return cache.value;
  }
  const value = Object.freeze(mergeRuntimeConfig(base, embeddedRuntimeConfigOverrides));
  cache = { base, overrides: embeddedRuntimeConfigOverrides, value };
  return value;
}

/**
 * Embedded-host runtime-config overrides. Ported from insights-home for a
 * future integration adapter; kept here (not on the `./core` barrel) because
 * nothing in this app calls them yet.
 */
export function setEmbeddedRuntimeConfigOverrides(overrides: Partial<AppRuntimeConfig> = {}): void {
  embeddedRuntimeConfigOverrides = overrides;
}

export function clearEmbeddedRuntimeConfigOverrides(): void {
  embeddedRuntimeConfigOverrides = {};
}

function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Optional typography family from runtime config; consumed by ThemeProvider. */
export function getRuntimeTypographyFont(): string | undefined {
  return asNonEmptyString(getRuntimeConfig().typographyFont);
}

/**
 * Origins (besides same-origin) trusted for cross-frame theme messages.
 * Consumed by `createThemeChannel` (src/providers/themeChannel.ts).
 */
export function getAllowedMessageOrigins(): string[] {
  const cfg = getRuntimeConfig().allowedMessageOrigins;
  if (!Array.isArray(cfg)) return [];
  return cfg
    .filter((origin): origin is string => typeof origin === "string")
    .map((origin) => origin.trim())
    .filter(Boolean)
    .filter((origin, index, arr) => arr.indexOf(origin) === index);
}

/** Router basename derived from `APP_CONFIG.basePath` (empty string means root). */
export function getAppBasePath(): string {
  const basePath = getRuntimeConfig().basePath ?? "/";
  return basePath === "/" ? "" : basePath.replace(/\/+$/, "");
}

/**
 * Canonical SPA entry pathname under the configured base path.
 * Root deploys use `/`; subpath deploys use a trailing slash, e.g. `/home/`.
 */
function getCanonicalAppEntryPath(): string {
  const base = getAppBasePath();
  return base ? `${base}/` : "/";
}

/**
 * Full URL to the SPA entry under the configured base path, e.g. `http://localhost:3000/home/`.
 * Use for Auth0 `returnTo` and hard navigations — `window.location.origin` alone breaks subpath deploys
 * and triggers Vite's "did you mean to visit /home/?" when base is `/home/`.
 */
export function getAppPublicRootUrl(): string {
  return `${window.location.origin}${getCanonicalAppEntryPath()}`;
}

/**
 * Where Auth0 returns the user after logout; it must exactly match an Allowed
 * Logout URL. At the root that's the bare origin (no trailing slash); under a
 * subpath, the app's entry URL.
 */
export function getAppLogoutReturnUrl(): string {
  return getAppBasePath() ? getAppPublicRootUrl() : window.location.origin;
}
